import { defaultsDeep } from 'lodash';
import { getMuiTheme } from 'material-ui/styles';
import { createMuiTheme } from '@material-ui/core/styles';
import { fade, darken } from '@material-ui/core/styles/colorManipulator';
import getPropsLuma from './getPropsLuma';
import getPropsLumaDark from './getPropsLumaDark';
import getPropsLumaLegacy from './getPropsLumaLegacy';

const fontFamily = '"freight-sans-pro",sans-serif';

/*
  IMPORTANT:
  We are migrating Luma components and style guides. All color hard code declaration will be removed from pages and moved to here.
  From now, all color should be placed here and have a name defined by Jennifer.
  We need to migrate and refactor all luma style code before we be able to really take advatages from material-ui themes
*/
export const colorAccentAloe = '#59BF94';
const colorAccentLavander = '#B6BFE7';
const colorAccentLotus = '#F5B9CF';
const colorAccentLuma = '#F37021';
const colorAccentPeach = '#FFDBC6';
const colorAccentPeachLight = '#FFF3EB';
const colorAccentOrange = '#FFC24D';
const colorAccentSky = '#83DBEE';
const colorAccentSunrise = '#FFEBC4';
const colorAccentWatermelon = '#D7797E';
const colorSupportAqua = '#B3E3E2';
const colorSupportMidnight = '#182538';
const colorSupportPowder = '#CDE7ED';
const colorSupportSlate = '#4E6170';
const colorSupportSnow = '#F0F6F7';
const colorSupportSteel = '#617988';
const colorAccentTeal = '#2899CA';

// custom color declaration, we're migrating hard coded coors to color variables
// we need to finish this refactor step as a requirement to work eith a theme file properly
const primary1Color = colorSupportMidnight;
const accent1Color = colorAccentLuma;
const accent2Color = fade(accent1Color, 0.4);
const textColor = colorSupportMidnight;

const colorSuccess = '#25A575';
const colorSuccessHover = darken(colorSuccess, 0.2);
const colorDanger = colorAccentWatermelon;
const colorDangerHover = darken(colorDanger, 0.2);
const colorInfo = fade(primary1Color, 0.6);
const colorSuccessBackground = '#DCF7ED';
const colorGrayishBlue = '#D0D4D6';
const colorLightGrayishBlue = '#E8EAED';
const colorDarkPink = '#B22E56';
const colorLightGrayishPink = '#FAE1E8';
export const colorLightBlue = '#BFDCE3';
export const colorDarkCyan = '#2595A5';
export const colorDarkGray = '#808080';

export const colorOfferPast = accent2Color;
export const colorLightYellow = '#FFE54D';
export const colorVeryLightGray = '#F2F2F2';
export const colorSupportGray = '#E9EFF0';

// colors we may dont need anymore
export const colorDarkModerateBlue = '#3A719B';
export const colorMostlyDesaturatedDarkBlue = '#D9D9D9';

const palette = {
  type: 'light',
  primary1Color,
  accent1Color,
  accent2Color,
  textColor,
  custom: {
    colorSuccess,
    colorSuccessHover,
    colorDanger,
    colorDangerHover,
    colorInfo,
    colorSuccessBackground,
    colorGrayishBlue,
    colorLightGrayishBlue,
    colorMostlyDesaturatedDarkBlue,
    colorDarkGray,
    colorDarkPink,
    colorLightGrayishPink,
    colorDarkModerateBlue,
    colorLightBlue,
    colorDarkCyan,
    colorVeryLightGray,
    colorSupportGray,
    colorSupportSlate,
    colorSupportSnow,
    colorOfferPast,
    colorLightYellow,
    colorAccentAloe,
    colorAccentLavander,
    colorAccentLotus,
    colorAccentLuma,
    colorAccentPeach,
    colorAccentPeachLight,
    colorAccentOrange,
    colorAccentSky,
    colorAccentSunrise,
    colorAccentWatermelon,
    colorSupportAqua,
    colorSupportMidnight,
    colorSupportPowder,
    colorSupportSteel,
    colorAccentTeal,
  },
};

/**
 * DEPRECATED: Themes for material-ui alpha version
 */
const deprecatedMuiTheme = getMuiTheme({
  fontFamily,
  palette,
  toolbar: {
    backgroundColor: 'transparent',
  },
  toggle: {
    thumbOffColor: colorVeryLightGray,
  },
});

/**
 * Themes for @material-ui
 */
const props = {
  typography: {
    useNextVariants: true,
    fontFamily,
  },
  palette: {
    background: {
      default: colorSupportSnow,
    },
    primary: {
      main: primary1Color,
    },
    secondary: {
      main: accent1Color,
    },
    tertiary: {
      main: '#fff',
    },
    action: {
      hover: colorLightGrayishBlue,
    },
    text: {
      secondary: colorSupportSteel,
    },
  },
  overrides: {
    MuiInput: {
      input: {
        '&::placeholder': {
          color: colorSupportSteel,
          fontSize: 16,
          opacity: 1,
        },
        '&::-ms-clear': {
          display: 'none',
        },
        '& ::-ms-clear': {
          display: 'none',
        },
        '&::-ms-reveal': {
          display: 'none',
        },
        '& ::-ms-reveal': {
          display: 'none',
        },
        '&:invalid': {
          boxShadow: 'none',
        },
        '&:-moz-submit-invalid': {
          boxShadow: 'none',
        },
        '&:-moz-ui-invalid': {
          boxShadow: 'none',
        },
        '& :invalid': {
          boxShadow: 'none',
        },
        '& :-moz-submit-invalid': {
          boxShadow: 'none',
        },
        '& :-moz-ui-invalid': {
          boxShadow: 'none',
        },
      },
    },
    // https://app.clickup.com/t/fb5gq
    MuiIconButton: {
      colorPrimary: {
        color: colorSupportSlate,
      },
    },
    MuiFormControlLabel: {
      label: {
        color: primary1Color,
      },
    },
    MuiFormHelperText: {
      root: {
        cursor: 'default',
      },
    },
    MuiIcon: {
      colorPrimary: {
        color: colorSupportSlate,
      },
      fontSizeSmall: {
        fontSize: 18,
      },
      fontSizeLarge: {
        fontSize: 32,
      },
    },
    MuiSvgIcon: {
      colorPrimary: {
        color: primary1Color,
      },
      fontSizeSmall: {
        fontSize: 18,
      },
      fontSizeLarge: {
        fontSize: 32,
      },
    },
    MuiButton: {
      root: {
        color: primary1Color,
      },
      sizeLarge: {
        fontSize: 17,
      },
    },
    MuiDialogActions: {
      root: {
        padding: 16,
        margin: 0,
        background: colorVeryLightGray,
      },
    },
    MuiFormLabel: {
      asterisk: {
        color: colorDanger,
      },
    },
    // WORKAROUND reverging primary color for icons changed on https://app.clickup.com/t/fb5gq
    LumaIconButton: {
      colorPrimary: {
        color: primary1Color,
      },
    },
  },
  custom: {
    fontSizes: {
      title: 42,
      largeIcon: 32,
      heading: 24,
      subHeading: 20,
      bodyG: 18,
      body: 17,
      bodyS: 16,
      small: 14,
      tiny: 12,
    },
    palette: palette.custom,
    // TODO: use props section
    // https://material-ui.com/customization/themes/#properties
    MuiInput: {
      disableUnderline: false,
    },
    MuiDrawer: {
      width: 216,
    },
  },
};

const theme = createMuiTheme(props);
const muiThemeLuma = createMuiTheme(getPropsLuma(theme, props));
const muiThemeLumaDark = createMuiTheme(getPropsLumaDark(theme, getPropsLuma(theme, props)));
const muiThemeLumaLegacy = createMuiTheme(getPropsLumaLegacy(theme, props));
const makeMuiThemeLumaLegacy = (customTheme) => createMuiTheme(getPropsLumaLegacy(customTheme, props));
const makeMuiCustomStyles = (customTheme) => createMuiTheme(defaultsDeep(customTheme, getPropsLuma(theme, props)));

export {
  deprecatedMuiTheme,
  muiThemeLuma,
  muiThemeLumaDark,
  muiThemeLumaLegacy,
  makeMuiThemeLumaLegacy,
  makeMuiCustomStyles,
};
